import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import toast from 'react-hot-toast';
import { Clock } from 'lucide-react';

/*
 * Auto sign-out after a stretch of no activity, so a dashboard left open on an
 * unattended screen does not stay signed in. This is a convenience guard on
 * top of the server-side session — it clears the token in this browser and
 * sends the person back to the login page; it does not touch any data.
 *
 * The chosen length lives in localStorage (set from Settings), so it is a
 * per-browser preference. 0 means the feature is off.
 */

export const IDLE_KEY = 'wpm_idle_minutes';
const ACTIVITY_KEY = 'wpm_last_activity';   // shared across tabs of this site
const DEFAULT_MINUTES = 30;
const WARN_SECONDS = 60;                    // warn this long before signing out

export function getIdleMinutes() {
  const raw = parseInt(localStorage.getItem(IDLE_KEY), 10);
  if (Number.isNaN(raw)) return DEFAULT_MINUTES; // no choice saved yet → sensible default
  return Math.max(0, raw);
}

const now = () => Date.now();
const readActivity = () => {
  const t = parseInt(localStorage.getItem(ACTIVITY_KEY), 10);
  return Number.isNaN(t) ? now() : t;
};

export default function IdleLogout() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [minutes, setMinutes] = useState(getIdleMinutes);
  const [warnLeft, setWarnLeft] = useState(0); // seconds left while the warning shows; 0 = hidden
  const lastWrite = useRef(0);

  // Record activity — throttled, and shared with other tabs so working in one
  // keeps the others alive too.
  const markActive = useCallback(() => {
    const t = now();
    if (t - lastWrite.current > 2000) {
      lastWrite.current = t;
      try { localStorage.setItem(ACTIVITY_KEY, String(t)); } catch { /* private mode */ }
    }
    setWarnLeft(0);
  }, []);

  const doLogout = useCallback(() => {
    setWarnLeft(0);
    logout();
    try { localStorage.removeItem(ACTIVITY_KEY); } catch { /* ignore */ }
    navigate('/login', { replace: true });
    toast('Signed out — the dashboard was idle', { icon: '🔒', duration: 6000 });
  }, [logout, navigate]);

  // Keep the chosen length in sync with Settings (same tab via a custom event,
  // other tabs via the storage event).
  useEffect(() => {
    const refresh = () => setMinutes(getIdleMinutes());
    const onStorage = (e) => {
      if (!e || e.key === IDLE_KEY || e.key === null) refresh();
      if (e && e.key === ACTIVITY_KEY) setWarnLeft(0); // activity in another tab
    };
    window.addEventListener('wpm:idle-settings', refresh);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('wpm:idle-settings', refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    if (!minutes) { setWarnLeft(0); return; } // feature off

    const events = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll', 'click'];
    events.forEach(ev => window.addEventListener(ev, markActive, { passive: true }));
    markActive(); // reset the clock when the feature turns on

    const limitMs = minutes * 60 * 1000;

    // One check drives both the warning and the sign-out. Runs on an interval
    // and again whenever the tab regains focus (background tabs get throttled,
    // so the interval alone can miss a long absence).
    const check = () => {
      if (!localStorage.getItem('wpm_token')) return; // already signed out elsewhere
      const idle = now() - readActivity();
      if (idle >= limitMs) { doLogout(); return; }
      const toGo = Math.ceil((limitMs - idle) / 1000);
      setWarnLeft(toGo <= WARN_SECONDS ? toGo : 0);
    };

    const id = setInterval(check, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    check();

    return () => {
      events.forEach(ev => window.removeEventListener(ev, markActive));
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, [minutes, markActive, doLogout]);

  if (!warnLeft) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 16,
        boxShadow: 'var(--shadow)', padding: '22px 24px', width: 'min(400px, 100%)', textAlign: 'center',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 13, margin: '0 auto 14px',
          background: 'var(--amber-bg)', color: 'var(--amber)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Clock size={22} />
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>
          Still there?
        </h3>
        <p style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.55, margin: '0 0 18px' }}>
          You’ve been idle for a while. You’ll be signed out in{' '}
          <strong style={{ color: 'var(--text)' }}>{warnLeft}</strong> second{warnLeft !== 1 ? 's' : ''}.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn" onClick={doLogout}>Log out now</button>
          <button className="btn primary" onClick={markActive}>Stay signed in</button>
        </div>
      </div>
    </div>
  );
}
